import { useState, useEffect } from "react";
import { useAction } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { useTranslation } from "@/packages/utils";
import { motion } from "framer-motion";
import { Download, X, Loader2, CheckCircle2, Database } from "lucide-react";
import { Button } from "@/components/ui/primitives";

type ExportScope = "full" | "workspace";
type SeedBackupKey = "golden_backup" | "golden_backup_demo_workspace";

type SeedBackupInfo = {
  exists: boolean;
  createdAt?: number;
  tableCount?: number;
  recordCount?: number;
  scope?: "full" | "workspace";
  sourceWorkspaceId?: Id<"workspaces">;
};

const fullResetBackupKey: SeedBackupKey = "golden_backup";
const demoWorkspaceBackupKey: SeedBackupKey = "golden_backup_demo_workspace";

export function BackupExportPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(["admin"]);
  const exportDb = useAction(api.admin_backup.exportDatabase);
  const listWorkspacesAction = useAction(api.admin_backup.listWorkspaces);
  const saveSeedBackupAction = useAction(api.admin_backup.saveSeedBackup);
  const getSeedBackupInfoAction = useAction(api.admin_backup.getSeedBackupInfo);

  const [scope, setScope] = useState<ExportScope>("full");
  const [workspaceId, setWorkspaceId] = useState<string>("");
  const [workspaces, setWorkspaces] = useState<
    Array<{ _id: Id<"workspaces">; name: string; slug: string }>
  >([]);
  const [status, setStatus] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);
  const [lastDownload, setLastDownload] = useState<{
    filename: string;
    timestamp: number;
  } | null>(null);
  const [fullSeedBackupInfo, setFullSeedBackupInfo] =
    useState<SeedBackupInfo | null>(null);
  const [demoSeedBackupInfo, setDemoSeedBackupInfo] =
    useState<SeedBackupInfo | null>(null);
  const [isSavingFullSeed, setIsSavingFullSeed] = useState(false);
  const [isSavingDemoSeed, setIsSavingDemoSeed] = useState(false);
  const [fullSeedStatus, setFullSeedStatus] = useState<string>("");
  const [demoSeedStatus, setDemoSeedStatus] = useState<string>("");

  const selectedWorkspace =
    workspaces.find((workspace) => workspace._id === workspaceId) ?? null;
  const demoSourceWorkspace =
    workspaces.find(
      (workspace) => workspace._id === demoSeedBackupInfo?.sourceWorkspaceId,
    ) ?? null;

  useEffect(() => {
    getSeedBackupInfoAction({ key: fullResetBackupKey })
      .then(setFullSeedBackupInfo)
      .catch(() => {
        // Not a superuser or error — silently ignore
      });

    getSeedBackupInfoAction({ key: demoWorkspaceBackupKey })
      .then(setDemoSeedBackupInfo)
      .catch(() => {
        // Not a superuser or error — silently ignore
      });
  }, [getSeedBackupInfoAction]);

  async function loadWorkspaces() {
    if (workspaces.length > 0) return;
    try {
      const result = await listWorkspacesAction({});
      setWorkspaces(result);
    } catch (err) {
      setStatus(
        `Error loading workspaces: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  async function handleSaveFullSeedBackup() {
    setIsSavingFullSeed(true);
    setFullSeedStatus(t("actions.seedBackup.savingFull"));
    try {
      const result = await saveSeedBackupAction({
        key: fullResetBackupKey,
        scope: "full",
      });
      setFullSeedStatus(
        t("actions.seedBackup.savedFull", {
          tableCount: result.tableCount,
          recordCount: result.recordCount,
        }),
      );
      const info = await getSeedBackupInfoAction({ key: fullResetBackupKey });
      setFullSeedBackupInfo(info);
    } catch (err) {
      setFullSeedStatus(
        `${t("actions.seedBackup.failedFull")}: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setIsSavingFullSeed(false);
    }
  }

  async function handleSaveDemoSeedBackup() {
    if (!selectedWorkspace) {
      setDemoSeedStatus(t("actions.seedBackup.selectWorkspaceForDemo"));
      return;
    }

    setIsSavingDemoSeed(true);
    setDemoSeedStatus(t("actions.seedBackup.savingDemo"));
    try {
      const result = await saveSeedBackupAction({
        key: demoWorkspaceBackupKey,
        scope: "workspace",
        workspaceId: selectedWorkspace._id,
      });
      setDemoSeedStatus(
        t("actions.seedBackup.savedDemo", {
          workspace: selectedWorkspace.name,
          tableCount: result.tableCount,
          recordCount: result.recordCount,
        }),
      );
      const info = await getSeedBackupInfoAction({
        key: demoWorkspaceBackupKey,
      });
      setDemoSeedBackupInfo(info);
    } catch (err) {
      setDemoSeedStatus(
        `${t("actions.seedBackup.failedDemo")}: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setIsSavingDemoSeed(false);
    }
  }

  async function handleExport() {
    if (scope === "workspace" && !workspaceId) {
      setStatus(t("actions.exportBackup.selectWorkspace"));
      return;
    }

    setIsExporting(true);
    setStatus(t("actions.exportBackup.fetchingRecords"));

    try {
      const result = await exportDb({
        scope,
        workspaceId: scope === "workspace" ? selectedWorkspace?._id : undefined,
      });

      const { tables, fileUrls } = result;

      const files: Record<string, string> = {};
      const fileEntries = Object.entries(fileUrls);
      let fetched = 0;

      for (const [storageId, url] of fileEntries) {
        setStatus(
          t("actions.exportBackup.fetchingFiles", {
            current: fetched + 1,
            total: fileEntries.length,
          }),
        );
        try {
          const response = await fetch(url);
          const buffer = await response.arrayBuffer();
          const base64 = btoa(
            new Uint8Array(buffer).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              "",
            ),
          );
          files[storageId] = base64;
        } catch {
          // Skip files that fail to download
        }
        fetched++;
      }

      const backup = {
        version: 1,
        createdAt: new Date().toISOString(),
        scope,
        workspaceId: scope === "workspace" ? workspaceId : null,
        tables,
        files,
      };

      const json = JSON.stringify(backup, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const filename = `backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.href = blobUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(blobUrl);

      setLastDownload({ filename, timestamp: Date.now() });

      const totalRecords = Object.values(tables).reduce(
        (sum, arr) => sum + (arr as unknown[]).length,
        0,
      );
      setStatus(
        t("actions.exportBackup.complete", {
          tables: Object.keys(tables).length,
          records: totalRecords,
          files: Object.keys(files).length,
        }),
      );
    } catch (err) {
      setStatus(
        `${t("actions.exportBackup.failed")}: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 backdrop-blur-[2px] pt-[8vh] pb-12"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-lg mx-4"
      >
        <div className="rounded-2xl border bg-card shadow-2xl shadow-black/10">
          {/* Header */}
          <div className="px-6 pt-5 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 ring-1 ring-blue-500/20">
                  <Download className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2
                    className="text-lg font-bold text-foreground"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {t("actions.exportBackup.label")}
                  </h2>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                    {t("actions.exportBackup.description")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Scope selection */}
          <div className="px-6 space-y-4">
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("actions.exportBackup.scope")}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setScope("full")}
                  className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-all ${
                    scope === "full"
                      ? "border-blue-300 bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                      : "border-border bg-background text-muted-foreground hover:border-border/80"
                  }`}
                >
                  <Database className="h-3.5 w-3.5" />
                  {t("actions.exportBackup.fullDatabase")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setScope("workspace");
                    loadWorkspaces();
                  }}
                  className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-all ${
                    scope === "workspace"
                      ? "border-blue-300 bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                      : "border-border bg-background text-muted-foreground hover:border-border/80"
                  }`}
                >
                  {t("actions.exportBackup.workspace")}
                </button>
              </div>
            </div>

            {scope === "workspace" && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("actions.exportBackup.selectWorkspace")}
                </p>
                <select
                  className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-2"
                  value={workspaceId}
                  onChange={(e) => setWorkspaceId(e.target.value)}
                >
                  <option value="">
                    {t("actions.exportBackup.selectWorkspacePlaceholder")}
                  </option>
                  {workspaces.map((w) => (
                    <option key={w._id} value={w._id}>
                      {w.name} ({w.slug})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Action bar */}
          <div className="px-6 pt-5 pb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("actions.cancel")}
            </button>
            <Button
              onClick={handleExport}
              disabled={isExporting}
              variant="primary"
              className="min-w-[140px] h-10"
            >
              {isExporting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t("actions.exportBackup.exporting")}
                </span>
              ) : (
                t("actions.exportBackup.download")
              )}
            </Button>
          </div>

          {/* Golden backup section for full reset */}
          {scope === "full" && (
            <div className="border-t border-border px-6 py-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 ring-1 ring-amber-500/20">
                  <Database className="h-4 w-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    {t("actions.seedBackup.fullLabel")}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                    {t("actions.seedBackup.fullDescription")}
                  </p>

                  {fullSeedBackupInfo?.exists && (
                    <div className="mt-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                      <p className="font-medium text-foreground">
                        {t("actions.seedBackup.currentFullBackup")}
                      </p>
                      <p>
                        {t("actions.seedBackup.savedAt", {
                          date: new Date(
                            fullSeedBackupInfo.createdAt!,
                          ).toLocaleString(),
                        })}
                      </p>
                      <p>
                        {t("actions.seedBackup.stats", {
                          tableCount: fullSeedBackupInfo.tableCount,
                          recordCount: fullSeedBackupInfo.recordCount,
                        })}
                      </p>
                    </div>
                  )}

                  {!fullSeedBackupInfo?.exists &&
                    fullSeedBackupInfo !== null && (
                      <p className="mt-2 text-xs text-muted-foreground/70 italic">
                        {t("actions.seedBackup.noFullBackup")}
                      </p>
                    )}

                  <Button
                    onClick={handleSaveFullSeedBackup}
                    disabled={isSavingFullSeed}
                    variant="secondary"
                    className="mt-3 h-8 text-xs"
                  >
                    {isSavingFullSeed ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {t("actions.seedBackup.savingFull")}
                      </span>
                    ) : (
                      t("actions.seedBackup.saveFull")
                    )}
                  </Button>

                  {fullSeedStatus && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {fullSeedStatus}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Golden backup section for demo onboarding */}
          {scope === "workspace" && (
            <div className="border-t border-border px-6 py-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/20">
                  <Database className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    {t("actions.seedBackup.demoLabel")}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                    {t("actions.seedBackup.demoDescription")}
                  </p>

                  {demoSeedBackupInfo?.exists && (
                    <div className="mt-2 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                      <p className="font-medium text-foreground">
                        {t("actions.seedBackup.currentDemoBackup")}
                      </p>
                      <p>
                        {t("actions.seedBackup.savedAt", {
                          date: new Date(
                            demoSeedBackupInfo.createdAt!,
                          ).toLocaleString(),
                        })}
                      </p>
                      <p>
                        {t("actions.seedBackup.stats", {
                          tableCount: demoSeedBackupInfo.tableCount,
                          recordCount: demoSeedBackupInfo.recordCount,
                        })}
                      </p>
                      {demoSeedBackupInfo.sourceWorkspaceId && (
                        <p>
                          {t("actions.seedBackup.sourceWorkspace", {
                            workspace: demoSourceWorkspace
                              ? `${demoSourceWorkspace.name} (${demoSourceWorkspace.slug})`
                              : demoSeedBackupInfo.sourceWorkspaceId,
                          })}
                        </p>
                      )}
                    </div>
                  )}

                  {!demoSeedBackupInfo?.exists &&
                    demoSeedBackupInfo !== null && (
                      <p className="mt-2 text-xs text-muted-foreground/70 italic">
                        {t("actions.seedBackup.noDemoBackup")}
                      </p>
                    )}

                  <Button
                    onClick={handleSaveDemoSeedBackup}
                    disabled={isSavingDemoSeed || !selectedWorkspace}
                    variant="secondary"
                    className="mt-3 h-8 text-xs"
                  >
                    {isSavingDemoSeed ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {t("actions.seedBackup.savingDemo")}
                      </span>
                    ) : (
                      t("actions.seedBackup.saveDemo")
                    )}
                  </Button>

                  {demoSeedStatus && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {demoSeedStatus}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Status / result */}
          {status && (
            <div className="border-t border-border bg-muted/30 px-6 py-4 rounded-b-2xl">
              <p className="text-xs text-muted-foreground">{status}</p>
            </div>
          )}

          {lastDownload && !status.includes("failed") && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-green-200 bg-green-50 px-6 py-4 rounded-b-2xl"
            >
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                <div>
                  <p className="text-sm font-semibold text-green-800">
                    {lastDownload.filename}
                  </p>
                  <p className="mt-0.5 text-xs text-green-700/70">
                    {new Date(lastDownload.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
